import { callable } from '../callable';

// ─── Assisted evaluation-standard authoring (Delivery 3a) ─────────────────────
// Thin wrappers over the staff-only callables. The web never says "IA" — the
// vocabulary is "la plataforma / sugerido / propuesta" (see the sis i18n keys).

export type StandardProvenance = 'teacher_literal' | 'platform_reworded' | 'platform_inferred';

export interface ScaffoldAxisDto {
  id: string;
  /** i18n key (ns `sis`), resolved in the UI — never a loose string. */
  label: string;
  hint?: string;
}

export interface StructuralScaffoldDto {
  id: string;
  topicKey: string;
  /** i18n key (ns `sis`). */
  title: string;
  axes: ScaffoldAxisDto[];
  version: number;
}

export interface AxisCoverageDto {
  axisId: string;
  content: string | null;
  provenance: StandardProvenance;
  gap: { question: string } | null;
}

export interface StandardContentDto {
  axes: AxisCoverageDto[];
  tensions: string[];
}

export interface EvaluationStandardDto {
  id: string;
  tenantId: string;
  scaffoldId: string;
  scaffoldVersion: number;
  title: string;
  catalogYear: string;
  status: 'draft' | 'approved';
  content: StandardContentDto;
  authoredBy: string;
  aiAssisted: boolean;
  /** Practice root-question bank (Frente B). Empty = no self-study offered. */
  practiceRootQuestions?: string[];
  approvedBy?: string;
  approvedAt?: string;
  createdAt: string;
  updatedAt: string;
}

export interface GenerateStandardResult {
  standard: EvaluationStandardDto;
  /** Axes the platform reclassified from literal → reworded — surfaced in the UI. */
  degradedAxisIds: string[];
  usage: { input: number; output: number };
  estimatedCostUSD: number;
}

export const evaluationStandardApi = {
  listScaffolds(data: { tenantId: string }): Promise<{ scaffolds: StructuralScaffoldDto[] }> {
    const fn = callable<typeof data, { scaffolds: StructuralScaffoldDto[] }>('listScaffolds');
    return fn(data).then((r) => r.data);
  },

  generateDraft(data: {
    tenantId: string;
    scaffoldId: string;
    teacherProse: string;
    title: string;
    catalogYear: string;
    locale: string;
    requestedTopic?: string;
  }): Promise<GenerateStandardResult> {
    const fn = callable<typeof data, GenerateStandardResult>('generateStandardDraft');
    return fn(data).then((r) => r.data);
  },

  approve(data: {
    tenantId: string;
    standardId: string;
  }): Promise<{ standard: EvaluationStandardDto }> {
    const fn = callable<typeof data, { standard: EvaluationStandardDto }>('approveStandard');
    return fn(data).then((r) => r.data);
  },

  get(data: {
    tenantId: string;
    standardId: string;
  }): Promise<{ standard: EvaluationStandardDto }> {
    const fn = callable<typeof data, { standard: EvaluationStandardDto }>('getStandardForEdit');
    return fn(data).then((r) => r.data);
  },

  list(data: {
    tenantId: string;
    catalogYear?: string;
  }): Promise<{ standards: EvaluationStandardDto[] }> {
    const fn = callable<typeof data, { standards: EvaluationStandardDto[] }>('listStandards');
    return fn(data).then((r) => r.data);
  },

  /** Save the practice root-question bank for a standard (Frente B). */
  savePracticeQuestions(data: {
    tenantId: string;
    standardId: string;
    questions: string[];
  }): Promise<{ standard: EvaluationStandardDto }> {
    const fn = callable<typeof data, { standard: EvaluationStandardDto }>('savePracticeQuestions');
    return fn(data).then((r) => r.data);
  },
};
